import { useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { addDoc, collection } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { db, storage } from "../lib/firebase";
import { compressImage } from "../lib/compressImage";
import { useAuth } from "../hooks/useAuth";
import type { IssueCategory } from "../types";

const categories: IssueCategory[] = ["Grün", "Fairway", "Bunker", "Weg", "Abschlag", "Sonstiges"];

export function ReportIssue() {
  const { clubId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [text, setText] = useState("");
  const [category, setCategory] = useState<IssueCategory>("Fairway");
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPhoto(file);
    setPreview(file ? URL.createObjectURL(file) : null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!clubId || !text.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const photoURLs: string[] = [];
      if (photo) {
        const compressed = await compressImage(photo);
        const path = `clubs/${clubId}/issues/${Date.now()}.jpg`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, compressed, { contentType: "image/jpeg" });
        photoURLs.push(await getDownloadURL(storageRef));
      }

      await addDoc(collection(db, "clubs", clubId, "issues"), {
        text: text.trim(),
        category,
        photoURLs,
        status: "neu",
        userId: user?.uid ?? "anonym",
        userName: user?.email ?? "Anonym",
        createdAt: Date.now(),
      });

      setDone(true);
    } catch {
      setError("Meldung konnte nicht gespeichert werden. Bitte erneut versuchen.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-6 pb-24 text-center">
        <div className="animate-fade-up marker-badge h-16 w-16 border-0 bg-turf-100 text-turf-700">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-8 w-8">
            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <h1 className="animate-fade-up mt-5 font-display text-2xl font-semibold text-turf-900" style={{ animationDelay: "80ms" }}>
          Danke für deine Meldung
        </h1>
        <p className="animate-fade-up mt-2 text-ink-600" style={{ animationDelay: "140ms" }}>
          Das Greenkeeping-Team wurde informiert.
        </p>
        <button
          onClick={() => navigate(`/club/${clubId}`)}
          className="mt-8 h-12 w-full rounded-xl bg-turf-700 font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985]"
        >
          Zurück zum Dashboard
        </button>
      </main>
    );
  }

  return (
    <main className="mx-auto min-h-dvh max-w-md px-5 pb-24 pt-6">
      <Link to={`/club/${clubId}`} className="inline-flex items-center gap-1 text-sm font-medium text-turf-700">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4">
          <path d="m15 6-6 6 6 6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Zurück zum Dashboard
      </Link>

      <h1 className="mt-4 font-display text-[1.75rem] font-semibold tracking-tight text-turf-900">Problem melden</h1>
      <p className="mt-1 text-sm text-ink-600">Beschreib kurz, was dir aufgefallen ist.</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-5">
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-900">Kategorie</legend>
          <div className="grid grid-cols-3 gap-2">
            {categories.map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
                className={`h-11 rounded-xl text-sm font-medium transition-all ${
                  category === c
                    ? "bg-turf-700 text-chalk-50 shadow-sm"
                    : "card text-ink-900"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </fieldset>

        <div>
          <label htmlFor="issue-text" className="mb-1.5 block text-sm font-medium text-ink-900">
            Beschreibung
          </label>
          <textarea
            id="issue-text"
            required
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="z. B. Großes Loch am Abschlag 7…"
            className="w-full rounded-xl border border-chalk-200 bg-white p-4 text-base outline-none transition-colors focus:border-turf-600"
          />
        </div>

        <div>
          <span className="mb-1.5 block text-sm font-medium text-ink-900">Foto (optional)</span>
          <label
            htmlFor="issue-photo"
            className="flex h-32 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-chalk-200 bg-white text-sm text-ink-400 transition-colors hover:border-turf-400"
          >
            {preview ? (
              <img src={preview} alt="Vorschau" className="h-full w-full rounded-xl object-cover" />
            ) : (
              <>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" className="mb-1.5 h-7 w-7">
                  <path d="M4 16.5V6a2 2 0 0 1 2-2h3l1.5-1.5h3L15 4h3a2 2 0 0 1 2 2v10.5M4 16.5 8 12l3 3 4-5 5 6.5M4 16.5V19a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Foto aufnehmen oder auswählen
              </>
            )}
          </label>
          <input
            id="issue-photo"
            type="file"
            accept="image/*"
            onChange={handlePhotoChange}
            className="sr-only"
          />
        </div>

        {error && (
          <p role="alert" className="rounded-lg bg-flag-100 px-3 py-2 text-sm text-flag-600">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || !text.trim()}
          className="h-12 w-full rounded-xl bg-turf-700 text-base font-semibold text-chalk-50 shadow-sm transition-transform active:scale-[0.985] disabled:opacity-50"
        >
          {submitting ? "Wird gesendet…" : "Meldung senden"}
        </button>
      </form>
    </main>
  );
}
